import {
  DiffAction,
  FeeConfigurationExport,
  FeeConfigurationViewModel,
  FeeHudLineExport,
  FeeHudLineViewModel,
  FeeRuleExport,
  FeeRuleViewModel,
  FeeRuleType,
  FeeUpdateType,
  IntegrationVendor,
  SettingDataExport,
  SettingDataViewModel,
} from '../models/fees';

export interface PublicationSnapshot {
  hudLines: FeeHudLineExport[];
  configurations: FeeConfigurationExport[];
}

export interface ComparisonRuleDiff {
  action: DiffAction;
  ruleId: string;
  left: FeeRuleExport | null;
  right: FeeRuleExport | null;
  changedFields: string[];
}

export interface ComparisonConfigDiff {
  action: DiffAction;
  feeConfigurationGuid: string;
  hudLine: string;
  left: FeeConfigurationExport | null;
  right: FeeConfigurationExport | null;
  changedFields: string[];
  ruleDiffs: ComparisonRuleDiff[];
}

export interface ComparisonHudLineDiff {
  action: DiffAction;
  hudLine: string;
  left: FeeHudLineExport | null;
  right: FeeHudLineExport | null;
  changedFields: string[];
}

export interface PublicationComparison {
  hudLineDiffs: ComparisonHudLineDiff[];
  configurationDiffs: ComparisonConfigDiff[];
}

const toSettingDataExport = (settingData: SettingDataViewModel): SettingDataExport => ({
  fieldId: settingData.fieldId ?? '',
  value: settingData.value,
  updateType: settingData.updateType ?? FeeUpdateType.SetValue,
  isDateBased: settingData.isDateBased,
  dateFieldPath: settingData.dateFieldPath,
  dateValues: settingData.dateValues,
});

export const toHudLineExport = (hudLine: FeeHudLineViewModel): FeeHudLineExport => ({
  hudLine: hudLine.hudLine ?? '',
  hudSection: hudLine.hudSection ?? '',
  description: hudLine.description ?? '',
  loanEstimateSection: hudLine.loanEstimateSection ?? '',
  closingDisclosureSection: hudLine.closingDisclosureSection ?? '',
  losVendor: hudLine.losVendor ?? IntegrationVendor.None,
  group: hudLine.group,
  groupDetails: hudLine.groupDetails,
  isAprAffiliate: hudLine.isAprAffiliate,
  isAprNonAffiliate: hudLine.isAprNonAffiliate,
  estimateIncludedInEscrow: hudLine.estimateIncludedInEscrow,
  borrowerCanShopFor: hudLine.borrowerCanShopFor,
  paidTo: hudLine.paidTo,
  restrictions: hudLine.restrictions ?? ({} as FeeHudLineExport['restrictions']),
  losMetadata: hudLine.losMetadata ?? [],
});

const toRuleExport = (rule: FeeRuleViewModel): FeeRuleExport => ({
  ruleId: rule.ruleId,
  description: rule.description ?? '',
  expression: rule.expression ? JSON.stringify(rule.expression) : null,
  ruleType: rule.ruleType ?? FeeRuleType.Final,
  settingData: (rule.settingData ?? []).map(toSettingDataExport),
});

export const toConfigurationExport = (
  config: FeeConfigurationViewModel,
  hudLineCode: string
): FeeConfigurationExport => ({
  feeConfigurationGuid: config.feeConfigurationGuid,
  hudLine: hudLineCode,
  description: config.description,
  rules: (config.rules ?? []).map(toRuleExport),
});

/**
 * null / undefined / '' are all "empty" as far as this diff cares — the backend omits null
 * fields from JSON entirely (JsonIgnoreCondition.WhenWritingNull), so the same logical "no
 * value" can arrive as undefined on one publication and null or '' on another. Without this,
 * those show up as a false diff even though they render identically ('-') to the user.
 */
function normalizeForCompare(value: unknown): unknown {
  return value === undefined || value === null || value === '' ? null : value;
}

function changedFieldsOf<T extends object>(left: T, right: T, fields: (keyof T)[]): string[] {
  return fields.filter(
    (field) => JSON.stringify(normalizeForCompare(left[field])) !== JSON.stringify(normalizeForCompare(right[field]))
  ) as string[];
}

export const HUD_LINE_COMPARABLE_FIELDS: (keyof FeeHudLineExport)[] = [
  'hudSection',
  'description',
  'loanEstimateSection',
  'closingDisclosureSection',
  'losVendor',
  'group',
  'groupDetails',
  'isAprAffiliate',
  'isAprNonAffiliate',
  'estimateIncludedInEscrow',
  'borrowerCanShopFor',
  'paidTo',
  'restrictions',
  'losMetadata',
];

export const CONFIG_COMPARABLE_FIELDS: (keyof FeeConfigurationExport)[] = ['description'];

export const RULE_COMPARABLE_FIELDS: (keyof FeeRuleExport)[] = [
  'description',
  'expression',
  'ruleType',
  'settingData',
];

function diffByKey<TExport extends object, TDiff>(
  leftItems: TExport[],
  rightItems: TExport[],
  keyOf: (item: TExport) => string,
  buildDiff: (action: DiffAction, left: TExport | null, right: TExport | null, changedFields: string[]) => TDiff,
  comparableFields: (keyof TExport)[]
): TDiff[] {
  const leftByKey = new Map(leftItems.map((item) => [keyOf(item), item]));
  const rightByKey = new Map(rightItems.map((item) => [keyOf(item), item]));
  const allKeys = new Set([...leftByKey.keys(), ...rightByKey.keys()]);

  return Array.from(allKeys).map((key) => {
    const left = leftByKey.get(key) ?? null;
    const right = rightByKey.get(key) ?? null;

    if (left && !right) {
      return buildDiff(DiffAction.Delete, left, null, []);
    }
    if (!left && right) {
      return buildDiff(DiffAction.Insert, null, right, []);
    }

    const changedFields = changedFieldsOf(left as TExport, right as TExport, comparableFields);
    const action = changedFields.length > 0 ? DiffAction.Update : DiffAction.Unchanged;
    return buildDiff(action, left, right, changedFields);
  });
}

/**
 * Diffs two fee-rule publication snapshots at the HUD-line, configuration, and nested-rule
 * level. Items are matched by their stable business key (hudLine code / feeConfigurationGuid /
 * ruleId) rather than DB id, since ids differ meaninglessly between draft and published rows.
 * A key present on only one side is Insert/Delete; present on both with differing fields is
 * Update; present on both and identical is Unchanged.
 */
export function comparePublications(left: PublicationSnapshot, right: PublicationSnapshot): PublicationComparison {
  const hudLineDiffs = diffByKey<FeeHudLineExport, ComparisonHudLineDiff>(
    left.hudLines,
    right.hudLines,
    (hudLine) => hudLine.hudLine,
    (action, hudLineLeft, hudLineRight, changedFields) => ({
      action,
      hudLine: (hudLineLeft ?? hudLineRight)!.hudLine,
      left: hudLineLeft,
      right: hudLineRight,
      changedFields,
    }),
    HUD_LINE_COMPARABLE_FIELDS
  );

  const leftConfigsByKey = new Map(left.configurations.map((c) => [c.feeConfigurationGuid, c]));
  const rightConfigsByKey = new Map(right.configurations.map((c) => [c.feeConfigurationGuid, c]));
  const allConfigKeys = new Set([...leftConfigsByKey.keys(), ...rightConfigsByKey.keys()]);

  const configurationDiffs: ComparisonConfigDiff[] = Array.from(allConfigKeys).map((key) => {
    const configLeft = leftConfigsByKey.get(key) ?? null;
    const configRight = rightConfigsByKey.get(key) ?? null;

    if (configLeft && !configRight) {
      return {
        action: DiffAction.Delete,
        feeConfigurationGuid: configLeft.feeConfigurationGuid,
        hudLine: configLeft.hudLine,
        left: configLeft,
        right: null,
        changedFields: [],
        ruleDiffs: [],
      };
    }
    if (!configLeft && configRight) {
      return {
        action: DiffAction.Insert,
        feeConfigurationGuid: configRight.feeConfigurationGuid,
        hudLine: configRight.hudLine,
        left: null,
        right: configRight,
        changedFields: [],
        ruleDiffs: [],
      };
    }

    const ruleDiffs = diffByKey<FeeRuleExport, ComparisonRuleDiff>(
      configLeft!.rules,
      configRight!.rules,
      (rule) => rule.ruleId,
      (action, ruleLeft, ruleRight, changedFields) => ({
        action,
        ruleId: (ruleLeft ?? ruleRight)!.ruleId,
        left: ruleLeft,
        right: ruleRight,
        changedFields,
      }),
      RULE_COMPARABLE_FIELDS
    );
    const rulesChanged = ruleDiffs.some((rd) => rd.action !== DiffAction.Unchanged);
    const changedFields = changedFieldsOf(configLeft as FeeConfigurationExport, configRight as FeeConfigurationExport, CONFIG_COMPARABLE_FIELDS);
    if (rulesChanged) {
      changedFields.push('rules');
    }

    return {
      action: changedFields.length > 0 ? DiffAction.Update : DiffAction.Unchanged,
      feeConfigurationGuid: configLeft!.feeConfigurationGuid,
      hudLine: configLeft!.hudLine,
      left: configLeft,
      right: configRight,
      changedFields,
      ruleDiffs,
    };
  });

  return { hudLineDiffs, configurationDiffs };
}
